import { useCallback, useEffect, useRef, useState } from "react";
import "./css/Wheel.scss";
import "./css/styles.scss";
import "./css/responsive.scss";
import "react-toastify/dist/ReactToastify.css";

import { ToastContainer } from "react-toastify";
import QuestionIcon from "./assets/questionIcon.svg";
import SoundUpIcon from "./assets/soundUp.svg";
import SoundMuteIcon from "./assets/soundMute.svg";
import HistoryIcon from "./assets/historyIcon.svg";
import DiamondIcon from "./assets/diamond.png";

import GameTable from "./GameMain/GameTable";
import WheeSpin from "./GameMain/WheeSpin";
import RulesModel from "./GameMain/RulesModel.";
import HistoryModel from "./GameMain/HistoryModel";
import RoundResultPanel from "./GameMain/RoundResultPanel";
import { buildMyResult, buildTopWinners } from "./GameMain/resultModel";

import { api, makeNonce } from "./api";
import { haptic, requestBalanceRefresh } from "./bridge";
import {
  connectSocket,
  subscribeLobby,
  on,
  disconnectSocket,
} from "./socket";

const soundPref = (() => {
  try { return JSON.parse(localStorage.getItem("sound")); } catch (_) { return false; }
})();

/**
 * Fallback payout estimate, used only when we don't know our own user id and so
 * cannot read the authoritative figure out of the broadcast `winners[]`. It is
 * labelled `est.` in the panel precisely because it can disagree with what the
 * settle transaction actually credited.
 */
function estimateRouletteWin(cfg, winningFrameIds, stakes) {
  let won = 0;
  for (const frameId of winningFrameIds || []) {
    const staked = Number((stakes || {})[frameId] || 0);
    if (staked <= 0) continue;
    const frame = ((cfg && cfg.frames) || []).find((f) => Number(f.id) === Number(frameId));
    won += Math.floor(staked * Number((frame && frame.multiplier) || 0));
  }
  return won;
}

function App() {
  const [config, setConfig] = useState(null);
  const [round, setRound] = useState(null);
  const [balance, setBalance] = useState(0);
  const [betsByFrame, setBetsByFrame] = useState({});
  const [myStakes, setMyStakes] = useState({});
  const [numberHistory, setNumberHistory] = useState([]);
  const [myBetHistory, setMyBetHistory] = useState([]);
  const [remainingMs, setRemainingMs] = useState(0);
  const [muteSound, setMuteSound] = useState(!!soundPref);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [result, setResult] = useState(null);
  const [fatalError, setFatalError] = useState(null);

  // Authoritative deadline (epoch ms) from the server; the local tick below
  // only interpolates between heartbeats and never decides anything.
  const deadlineRef = useRef(0);
  const roundRef = useRef(null);
  const myStakesRef = useRef({});
  const configRef = useRef(null);
  // Our own uid, so we can pick our row out of the broadcast `winners[]`.
  const currentUserIdRef = useRef(null);

  useEffect(() => { roundRef.current = round; }, [round]);
  useEffect(() => { myStakesRef.current = myStakes; }, [myStakes]);
  useEffect(() => { configRef.current = config; }, [config]);

  useEffect(() => {
    try { localStorage.setItem("sound", JSON.stringify(muteSound)); } catch (_) {}
  }, [muteSound]);

  const frames = (config && config.frames) || [];
  const quickBets = (config && config.quickBetAmounts) || [];
  const status = round ? round.status : "idle";

  /**
   * Remember our own uid from whichever response carries it. `/balance` and
   * `/round/current` return it directly; `POST /bet` echoes it back inside the
   * created bet, which is the path that keeps this working against a backend
   * that hasn't picked up the route change yet.
   */
  const captureUserId = useCallback((res) => {
    const id = res && (res.userId || (res.bet && res.bet.userId));
    if (id) currentUserIdRef.current = String(id);
  }, []);

  const refreshBalance = useCallback(async () => {
    try {
      const res = await api.getBalance();
      captureUserId(res);
      if (res && typeof res.balance === "number") setBalance(res.balance);
    } catch (_) {}
  }, [captureUserId]);

  const applyRoundPayload = useCallback((payload) => {
    if (!payload) return;
    captureUserId(payload);
    if (payload.round) {
      setRound(payload.round);
      setBetsByFrame(payload.round.betsByFrame || {});
    }
    if (typeof payload.balance === "number") setBalance(payload.balance);
    if (Array.isArray(payload.history)) setNumberHistory(payload.history);
    if (typeof payload.remainingMs === "number") {
      setRemainingMs(payload.remainingMs);
      deadlineRef.current = Date.now() + payload.remainingMs;
    }
    if (Array.isArray(payload.myBets)) {
      const stakes = {};
      for (const b of payload.myBets) {
        stakes[b.frameId] = (stakes[b.frameId] || 0) + Number(b.amount || 0);
      }
      setMyStakes(stakes);
    }
  }, [captureUserId]);

  // ---- bootstrap ----
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const cfgRes = await api.getConfig();
        if (cancelled) return;
        const cfg = cfgRes && cfgRes.config;
        setConfig(cfg);
        if (!cfg || !cfg.enabled) {
          setFatalError("disabled");
          return;
        }

        const [current, numbers] = await Promise.all([
          api.getCurrentRound(),
          api.getNumbers(cfg.historyLength || 20).catch(() => null),
        ]);
        if (cancelled) return;
        applyRoundPayload(current);
        if (numbers && Array.isArray(numbers.history)) setNumberHistory(numbers.history);

        await connectSocket();
        if (cancelled) return;
        subscribeLobby();
      } catch (e) {
        if (!cancelled) setFatalError(e && e.message ? e.message : "load_failed");
      }
    })();

    return () => { cancelled = true; };
  }, [applyRoundPayload]);

  // ---- socket wiring ----
  useEffect(() => {
    const offs = [];

    offs.push(on("roulette_state", (snap) => applyRoundPayload(snap)));
    offs.push(on("roulette_round_snapshot", (snap) => applyRoundPayload(snap)));

    offs.push(on("roulette_round_started", (p) => {
      setMyStakes({});
      setBetsByFrame({});
      setResult(null);
      setLastResult(null);
      applyRoundPayload(p);
    }));

    offs.push(on("roulette_bet_placed", (p) => {
      if (!roundRef.current || p.roundId !== roundRef.current._id) return;
      setBetsByFrame(p.betsByFrame || {});
    }));

    offs.push(on("roulette_round_spinning", (p) => {
      setRound((prev) => (prev && prev._id === p.roundId
        ? {
            ...prev,
            status: "spinning",
            winningNumber: p.winningNumber,
            winningFrameIds: p.winningFrameIds,
            spinEndsAt: p.spinEndsAt,
          }
        : prev));
      setLastResult(p.numberInfo || null);
      if (typeof p.remainingMs === "number") {
        setRemainingMs(p.remainingMs);
        deadlineRef.current = Date.now() + p.remainingMs;
      }
    }));

    offs.push(on("roulette_round_finished", (p) => {
      const finished = p.round;
      // Snapshot our stakes before applyRoundPayload runs: that function
      // rewrites myStakes from payload.myBets, which this event happens not to
      // carry. Pin the ordering rather than depend on it staying that way.
      const stakes = { ...(myStakesRef.current || {}) };
      applyRoundPayload(p);
      if (!finished) return;

      // Display only — the credit already happened server-side inside the
      // settle transaction. This renders exactly what the server decided.
      const cfg = configRef.current;
      const currentUserId = currentUserIdRef.current;
      const winners = Array.isArray(p.winners) ? p.winners : [];
      setResult({
        roundNumber: finished.roundNumber,
        winningNumber: finished.winningNumber,
        winningFrameIds: finished.winningFrameIds || [],
        myStakes: stakes,
        my: buildMyResult({
          winners,
          currentUserId,
          myStakes: stakes,
          estimatePayout: () => estimateRouletteWin(cfg, finished.winningFrameIds, stakes),
        }),
        top: buildTopWinners(winners, currentUserId),
      });

      refreshBalance();
      requestBalanceRefresh();
    }));

    offs.push(on("roulette_time_sync", (p) => {
      if (typeof p.remainingMs !== "number") return;
      setRemainingMs(p.remainingMs);
      deadlineRef.current = Date.now() + p.remainingMs;
      setRound((prev) => (prev && prev._id === p.roundId && prev.status !== p.status
        ? { ...prev, status: p.status }
        : prev));
    }));

    return () => { offs.forEach((fn) => fn && fn()); };
  }, [applyRoundPayload, refreshBalance]);

  // Local countdown between server heartbeats.
  useEffect(() => {
    const id = setInterval(() => {
      setRemainingMs(Math.max(0, deadlineRef.current - Date.now()));
    }, 200);
    return () => clearInterval(id);
  }, []);

  // The result panel must never sit over a live betting table. `round_started`
  // clears it on the happy path, but a reconnect can arrive as a snapshot that
  // flips status straight to `betting` without that event.
  useEffect(() => {
    if (status === "betting") setResult(null);
  }, [status]);

  // Flutter teardown hook — matches GenericGameScreen's `__ROULETTE_CLEANUP`.
  useEffect(() => {
    window.__ROULETTE_CLEANUP = () => {
      try { disconnectSocket(); } catch (_) {}
    };
    return () => {
      disconnectSocket();
      delete window.__ROULETTE_CLEANUP;
    };
  }, []);

  // ---- betting ----
  const placeBet = useCallback(async (frameId, amount) => {
    const currentRound = roundRef.current;
    if (!currentRound || currentRound.status !== "betting") return "betting_closed";
    if (!amount) return "no_coin_selected";
    if (balance < amount) return "insufficient_diamonds";

    haptic("light");
    try {
      const res = await api.bet({
        roundId: currentRound._id,
        frameId,
        amount,
        clientNonce: makeNonce(),
      });
      if (res && res.ok) {
        captureUserId(res);
        if (typeof res.balance === "number") setBalance(res.balance);
        setMyStakes((prev) => ({ ...prev, [frameId]: (prev[frameId] || 0) + amount }));
        return null;
      }
      return (res && res.error) || "bet_failed";
    } catch (e) {
      refreshBalance();
      return (e && e.body && e.body.error) || (e && e.message) || "bet_failed";
    }
  }, [balance, refreshBalance, captureUserId]);

  const openBetHistory = useCallback(async () => {
    setHistoryOpen(true);
    try {
      const res = await api.getMyBets(30);
      setMyBetHistory((res && res.bets) || []);
    } catch (_) {
      setMyBetHistory([]);
    }
  }, []);

  const handleOpenModel = useCallback((type) => {
    if (type === "rule") {
      setRulesOpen(true);
      setHistoryOpen(false);
    } else {
      setRulesOpen(false);
      openBetHistory();
    }
  }, [openBetHistory]);

  // Outcome-card inputs. `numberInfo` and `frames` both come from the config we
  // already fetched, so the panel needs no extra data from the server.
  const winningInfo = result
    ? ((config && config.numberInfo) || []).find((n) => Number(n.no) === Number(result.winningNumber)) || null
    : null;
  const winningFrames = result
    ? result.winningFrameIds
        .map((id) => frames.find((f) => Number(f.id) === Number(id)))
        .filter(Boolean)
    : [];

  if (fatalError) {
    return (
      <div style={{ overflow: "hidden" }}>
        <div className="row game-page">
          <div className="col-12 game-show" style={{ color: "#fff", padding: "40px", textAlign: "center" }}>
            {fatalError === "disabled"
              ? "Roulette Casino is currently unavailable."
              : "Couldn't load Roulette Casino. Please try again."}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ overflow: "hidden" }}>
      <div className="row game-page">
        <div className="col-0 col-lg-3" />
        <div className="col-12 col-lg-6 game-show">
          <div className="top-btn-show">
            <div className="helpIcon-button">
              <button
                onClick={(event) => {
                  handleOpenModel("rule");
                  event.stopPropagation();
                }}
              >
                <QuestionIcon />
              </button>
            </div>
            <div className="history-icon">
              <button
                onClick={(event) => {
                  handleOpenModel("history");
                  event.stopPropagation();
                }}
              >
                <HistoryIcon />
              </button>
            </div>
            <div className="sound-icon sound-icon-mo">
              <button onClick={() => setMuteSound((m) => !m)}>
                {muteSound ? <SoundMuteIcon /> : <SoundUpIcon />}
              </button>
            </div>
          </div>

          <div className="dimond-coin">
            <img src={DiamondIcon} alt="" />
            <span>{balance ? balance.toLocaleString() : "0"}</span>
          </div>

          <WheeSpin />

          <GameTable
            status={status}
            round={round}
            frames={frames}
            quickBets={quickBets}
            balance={balance}
            betsByFrame={betsByFrame}
            myStakes={myStakes}
            remainingMs={remainingMs}
            numberHistory={numberHistory}
            wheelNumbers={(config && config.wheelNumbers) || []}
            numberInfo={(config && config.numberInfo) || []}
            lastResult={lastResult}
            muteSound={muteSound}
            handleMuteSound={() => setMuteSound((m) => !m)}
            handleOpenModel={handleOpenModel}
            onBet={placeBet}
          />

          <RulesModel
            setOpen={setRulesOpen}
            open={rulesOpen}
            rule={config && config.rulesText}
          />
          <HistoryModel
            setOpen={setHistoryOpen}
            open={historyOpen}
            historyData={myBetHistory}
            frames={frames}
          />

          <RoundResultPanel
            open={!!result}
            roundNumber={result && result.roundNumber}
            myResult={result && result.my}
            topWinners={result && result.top}
            onClose={() => setResult(null)}
          >
            {result && (
              <div className="rr-outcome">
                <div className="rr-eyebrow">WINNING NUMBER</div>
                <div className={`rr-number rr-number--${(winningInfo && winningInfo.color) || "green"}`}>
                  {result.winningNumber}
                </div>
                <div className="rr-number-meta">
                  {winningInfo
                    ? `${winningInfo.color} · ${winningInfo.oddAndEven}`
                    : "—"}
                </div>
                {winningFrames.length > 0 && (
                  <ul className="rr-winning-frames">
                    {winningFrames.map((f) => (
                      <li
                        key={f.id}
                        className={Number(result.myStakes[f.id] || 0) > 0 ? "is-mine" : ""}
                      >
                        {f.label} ×{f.multiplier}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </RoundResultPanel>
        </div>
        <div className="col-0 col-lg-3" />
      </div>
      <ToastContainer />
    </div>
  );
}

export default App;
